import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { checkCredits, getUserTier, formatCredits } from '../../services/usageService';

/**
 * ListCleanerCreditEstimator - Shows credit estimation for filter jobs
 * 
 * This component displays:
 * - Estimated credits needed based on dataset rows
 * - Credit availability check
 * - Warnings for insufficient credits
 * - Pre-authorization status
 */
export default function ListCleanerCreditEstimator({ 
  rowCount = 0, 
  onCreditCheck = null,
  showTierLimits = true,
  autoCheck = true 
}) {
  const [creditCheck, setCreditCheck] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Perform credit check when row count changes
  useEffect(() => {
    if (autoCheck && rowCount > 0) {
      performCreditCheck();
    }
  }, [rowCount, autoCheck]);

  const performCreditCheck = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [checkResult, tierResult] = await Promise.all([
        checkCredits(rowCount),
        getUserTier()
      ]);
      
      if (checkResult.success) {
        setCreditCheck(checkResult.data);
        if (onCreditCheck) {
          onCreditCheck({
            ...checkResult.data,
            hasCredits: checkResult.data.hasCredits,
            rowCount: rowCount
          });
        }
      }
      
      if (tierResult.success) {
        setTierInfo(tierResult.data);
      }
    } catch (err) {
      console.error('Error checking credits:', err);
      setError('Failed to check credit availability');
    } finally {
      setLoading(false);
    }
  };

  // Don't show anything if no rows to estimate
  if (!rowCount || rowCount === 0) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Checking credit availability...</Typography>
        </Box>
      </Paper>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>Credit Check Unavailable</AlertTitle>
        {error}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">
            Estimated credits needed: <strong>{formatCredits(rowCount)}</strong>
          </Typography>
        </Box>
      </Alert>
    );
  }

  // Success state with credit information
  const hasCredits = creditCheck?.hasCredits ?? false;
  const creditsAvailable = creditCheck?.creditsAvailable ?? 0;
  const isUnlimited = creditsAvailable === -1;
  
  // Safely handle row count
  const safeRowCount = Math.max(0, rowCount || 0);
  
  // Determine severity based on credit availability
  const severity = hasCredits ? 'success' : 'error';
  const icon = hasCredits ? <CheckCircleIcon /> : <ErrorIcon />;
  
  // Wyspur brand colors
  const wyspurColors = {
    blue: '#0f71fb',    // Success/positive
    orange: '#ffaa01',  // Warning
    pink: '#df3e99'     // Error/critical
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Credit Estimation */}
      <Alert 
        severity={severity} 
        icon={icon}
        sx={{ 
          '& .MuiAlert-icon': {
            color: hasCredits ? wyspurColors.blue : wyspurColors.pink
          },
          borderColor: hasCredits ? wyspurColors.blue : wyspurColors.pink
        }}
      >
        <AlertTitle>
          Credit Cost
          <Tooltip title="Each row in your dataset consumes 1 credit">
            <IconButton size="small" sx={{ ml: 1 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AlertTitle>
        
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="body2">
            <strong>{formatCredits(safeRowCount)} credits</strong> will be charged • {' '}
            {isUnlimited ? (
              <span><strong>Unlimited</strong> available</span>
            ) : (
              <span><strong>{formatCredits(creditsAvailable)}</strong> available</span>
            )}
            {' '}({tierInfo?.displayName || tierInfo?.name || 'Unknown'} plan)
          </Typography>
        </Box>

        {/* Status chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<CalculateIcon />}
            label={`${safeRowCount} rows`}
            variant="outlined"
            size="small"
          />
          
          {hasCredits ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Credits available"
              variant="outlined"
              size="small"
              sx={{ 
                color: wyspurColors.blue,
                borderColor: wyspurColors.blue,
                '& .MuiChip-icon': { color: wyspurColors.blue }
              }}
            />
          ) : (
            <Chip
              icon={<ErrorIcon />}
              label="Insufficient credits"
              variant="outlined"
              size="small"
              sx={{ 
                color: wyspurColors.pink,
                borderColor: wyspurColors.pink,
                '& .MuiChip-icon': { color: wyspurColors.pink }
              }}
            />
          )}
          
          {!isUnlimited && creditCheck && (
            <Chip
              label={`${formatCredits(Math.max(0, creditsAvailable - safeRowCount))} remaining after`}
              variant="outlined"
              size="small"
              sx={{ 
                color: creditsAvailable - safeRowCount <= 0 ? wyspurColors.pink : 
                       creditsAvailable - safeRowCount < 100 ? wyspurColors.orange : 'inherit',
                borderColor: creditsAvailable - safeRowCount <= 0 ? wyspurColors.pink : 
                            creditsAvailable - safeRowCount < 100 ? wyspurColors.orange : 'inherit'
              }}
            />
          )}
        </Box>

        {/* Insufficient credits message */}
        {!hasCredits && !isUnlimited && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: wyspurColors.pink }}>
              Insufficient credits: Need {formatCredits(rowCount)}, have {formatCredits(creditsAvailable)} available.
            </Typography>
          </Box>
        )}

      </Alert>
    </Box>
  );
}